/**
 * Type representing validation errors as a record of field paths to error messages
 */
export type ValidationErrors = Record<string, string>;

/**
 * Checks if any validation error exists for a given path or its nested paths
 * @param path - The field path to check (supports dot notation)
 * @param errors - Object containing validation errors
 * @returns True if any error exists for the path or nested paths
 * @example checkAnyErrorForPath("user.name", { "user.name": "Required" }) => true
 */
export function checkAnyErrorForPath(path: string, errors: ValidationErrors): boolean {
    if (typeof path !== "string") return false;
    for (const key in errors) {
        if (key.startsWith(path)) return true;
    }
    return false;
}

/**
 * Retrieves the validation error message for a specific path
 * @param path - The field path to check
 * @param errors - Object containing validation errors
 * @returns Error message for the path, or empty string if none exists
 * @example retrieveErrorForPath("email", { email: "Invalid email" }) => "Invalid email"
 */
export function retrieveErrorForPath(path: string, errors: ValidationErrors): string {
    if (typeof path !== "string") return "";
    for (const key in errors) {
        if (key === path) return errors[key].toString();
    }
    return "";
}

/**
 * Sets a value at a nested path in an object (mutates the object)
 * @param obj - The object to modify
 * @param path - Dot-separated path to the property
 * @param value - The value to set
 * @example setValueAtPath({}, "user.name", "John") => { user: { name: "John" } }
 */
export function setValueAtPath(obj: any, path: string, value: any): void {
    const keys = path.split(".");
    keys.reduce((acc, key, index) => {
        if (index === keys.length - 1) {
            acc[key] = value;
        } else {
            if (!acc[key]) acc[key] = {};
            return acc[key];
        }
    }, obj);
}
